/**
 * Staff access and API keys. Every change is asked for first, approved by a second person — or,
 * when nobody else can, by the person who asked with a fresh authenticator code — and carried
 * out once. Sign-in details and API keys appear once, when the change is carried out.
 */

import { ChevronDown, KeyRound, ShieldCheck, UserPlus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { ApiCredential, RoleScopeType, StaffMember } from '../../api/areas/admin';
import type { CustomerSummary } from '../../api/areas/accounts';
import { REPORTING_CHANGE_KINDS, type AccessChange, type AccessChangeApplied, type AccessChangeDecision, type AccessChangeKind, type AccessChangeProposal } from '../../api/areas/operator';
import { Badge } from '../../components/Badge';
import { Callout, Field, Section } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CustomerPicker } from './RegisterDomain';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, SecretOnce, selectClass, type DialogBodyProps } from './shared';
import { resolveBaseUrl, scopeId as reportingScopeId } from '../../lib/reportingApi';

/** A reporting key is created by this change: applying it is "Lấy khoá" and shows the key once. */
const takesKey = (kind: AccessChangeKind) => kind === 'ISSUE_REPORTING_KEY' || kind === 'ROTATE_REPORTING_KEY';

const STATUS_TONE = { PENDING: 'pending', APPROVED: 'info', REJECTED: 'crit', EXPIRED: 'neutral', EXECUTED: 'ok', CANCELLED: 'neutral' } as const;

/** What a change applies to, in words. */
function TargetText({ c }: { c: AccessChange }) {
  const { t } = useTranslation('operator');
  const x = c.target;
  const role = x.role_code ? ` · ${x.role_code}${x.scope_type && x.scope_type !== 'GLOBAL' ? ` (${x.scope_name ?? x.scope_type})` : ''}` : '';
  if (c.kind === 'ISSUE_API_CREDENTIAL') return <span>{x.owner_name ?? x.owner_id}</span>;
  if (REPORTING_CHANGE_KINDS.includes(c.kind)) return <ReportingTarget c={c} />;
  if (c.kind === 'CREATE_STAFF') return <span>{`${x.display_name ?? ''} <${x.email ?? ''}>${x.roles?.length ? ` · ${x.roles.join(', ')}` : ''}`}</span>;
  return (
    <span>
      {x.user_id ? (
        <Link to={`/admin/staff/${x.user_id}`} className="font-medium text-primary hover:underline">
          {x.display_name ?? x.email}
        </Link>
      ) : (
        t('access.target')
      )}
      {role}
    </span>
  );
}

/** What a reporting change applies to: the place, and what changes there. */
function ReportingTarget({ c }: { c: AccessChange }) {
  const { t } = useTranslation('reporting');
  const x = c.target;
  const detail =
    c.kind === 'CHANGE_REPORTING_CONSUMER'
      ? [
          (x.datasets ?? []).map((d) => t(`scope.${reportingScopeId(d)}.label`)).join(', ') || t('place.nothing'),
          `${t('place.contacts')}: ${x.contacts_option ? t('yes') : t('no')}`,
        ].join(' · ')
      : takesKey(c.kind)
        ? [x.key?.name, x.key?.purpose ? t(`purpose.${x.key.purpose}`) : null, x.key?.allowed_cidrs.length ? t('keys.addresses', { count: x.key.allowed_cidrs.length }) : t('keys.anyAddress')].filter(Boolean).join(' · ')
        : x.dataset
          ? t(`dataset.${x.dataset as 'customers'}`, { defaultValue: x.dataset })
          : '';
  return (
    <span>
      <span className="font-medium">{x.consumer_name ?? x.consumer_id}</span>
      {detail ? <span className="text-muted-foreground"> · {detail}</span> : null}
    </span>
  );
}

/** A new reporting key: shown once, with the address to read from and what to do with it. */
function ReportingKeyOnce({ r }: { r: AccessChangeApplied }) {
  const { t } = useTranslation('reporting');
  const name = r.change.target.consumer_name ?? '';
  return (
    <div className="grid gap-3" data-reporting-secret>
      <Callout tone="warn" title={t('secret.title')}>
        {t('secret.warning', { name })}
      </Callout>
      {r.api_key ? <SecretOnce label={t('secret.key')} value={r.api_key} /> : null}
      {r.base_url ? <SecretOnce label={t('secret.address')} value={resolveBaseUrl(r.base_url, window.location.origin)} /> : null}
    </div>
  );
}

/** The one-time results of a carried-out change. */
function AppliedResult({ r }: { r: AccessChangeApplied }) {
  const { t } = useTranslation('operator');
  if (takesKey(r.change.kind)) {
    return (
      <div className="grid gap-3">
        <Callout tone="ok">{t('access.applied')}</Callout>
        <ReportingKeyOnce r={r} />
      </div>
    );
  }
  const secrets = Boolean(r.totp_uri || r.initial_password || r.api_key);
  return (
    <div className="grid gap-3">
      <Callout tone="ok">{t('access.applied')}</Callout>
      {secrets ? <Callout tone="warn">{t('common.showOnce')}</Callout> : null}
      {r.initial_password ? <SecretOnce label={t('access.initialPassword')} value={r.initial_password} /> : null}
      {r.totp_uri ? <SecretOnce label={t('access.totpUri')} value={r.totp_uri} hint={t('access.totpUriHint')} /> : null}
      {r.api_key ? <SecretOnce label={t('access.apiKey')} value={r.api_key} hint={t('access.apiKeyHint')} /> : null}
    </div>
  );
}

/**
 * What can be done with a change now: approve it yourself with an authenticator code, approve or
 * refuse someone else's, carry out an approved one — or wait for a colleague.
 */
function NextStep({ change, onChanged, close }: { change: AccessChange; onChanged: () => void; close: () => void }) {
  const { t } = useTranslation('operator');
  const { t: tr } = useTranslation('reporting');
  const [confirmed, setConfirmed] = useState({ hosting: false, run_history: false, viewers: false });
  // Turning customer contact data on: the approver confirms the receiving system's set-up.
  const confirms = change.kind === 'CHANGE_REPORTING_CONSUMER' && change.target.turns_contacts_on === true;
  const allConfirmed = !confirms || (confirmed.hosting && confirmed.run_history && confirmed.viewers);
  const applyLabel = takesKey(change.kind) ? tr('secret.takeKey') : t('access.apply');
  const [totp, setTotp] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [applied, setApplied] = useState<AccessChangeApplied | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const decide = useWrite((b: AccessChangeDecision, opts) => api.decideAccessChange(change.id, confirms && b.approve ? { ...b, confirmations: confirmed } : b, opts));
  const needsPassword = change.kind === 'CREATE_STAFF';

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.applyAccessChange(change.id, needsPassword && password.trim() ? { password: password.trim() } : {});
      setApplied(r);
      onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <div className="grid gap-4">
        <AppliedResult r={applied} />
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }

  const passwordField = needsPassword ? (
    <Field label={`${t('access.password')} (${t('common.optional')})`} hint={t('access.passwordHint')}>
      <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
    </Field>
  ) : null;

  if (change.can_self_approve) {
    return (
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void decide.run({ approve: true, reason: reason.trim(), totp: totp.trim() }).then((r) => {
            if (r) {
              onChanged();
              void apply();
            }
          });
        }}
      >
        <Callout tone="info" title={t('access.selfApproveTitle')}>
          {t('access.selfApproveHint')}
        </Callout>
        <ReasonField value={reason} onChange={setReason} label={t('access.decisionReason')} />
        <Field label={t('access.totp')}>
          <Input value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="w-32 font-mono" />
        </Field>
        {passwordField}
        {decide.state.phase === 'error' ? <WriteError error={decide.state.error} /> : null}
        {error ? <WriteError error={error} /> : null}
        <FormFooter busy={decide.busy || busy} disabled={totp.length !== 6 || !reason.trim()} submit={t('access.approveApply')} onClose={close} />
      </form>
    );
  }
  if (change.can_apply) {
    return (
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void apply();
        }}
      >
        {passwordField}
        {error ? <WriteError error={error} /> : null}
        <FormFooter busy={busy} submit={applyLabel} onClose={close} />
      </form>
    );
  }
  if (change.can_decide) {
    const name = change.target.consumer_name ?? '';
    return (
      <form className="grid gap-4" onSubmit={(e) => e.preventDefault()}>
        {confirms ? (
          <Callout tone="warn" title={tr('approval.contactsTitle', { name })}>
            <p>{tr('approval.contactsHint')}</p>
            <div className="mt-2 grid gap-2" data-contact-confirmations>
              {(['hosting', 'run_history', 'viewers'] as const).map((k) => (
                <label key={k} className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={confirmed[k]} onChange={(e) => setConfirmed((c) => ({ ...c, [k]: e.target.checked }))} />
                  <span>{tr(`receive.confirm.${k}`, { name })}</span>
                </label>
              ))}
            </div>
          </Callout>
        ) : null}
        <ReasonField value={reason} onChange={setReason} label={t('access.decisionReason')} />
        {decide.state.phase === 'error' ? <WriteError error={decide.state.error} /> : null}
        {decide.state.phase === 'done' ? <Callout tone="ok">{t(`access.status.${decide.state.result.status === 'APPROVED' ? 'APPROVED' : 'REJECTED'}`)}</Callout> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={close}>
            {t('common.close')}
          </Button>
          <Button type="button" variant="destructive" disabled={!reason.trim() || decide.busy || decide.state.phase === 'done'} onClick={() => void decide.run({ approve: false, reason: reason.trim() }).then((r) => r && onChanged())}>
            {t('access.reject')}
          </Button>
          <Button type="button" disabled={!reason.trim() || !allConfirmed || decide.busy || decide.state.phase === 'done'} onClick={() => void decide.run({ approve: true, reason: reason.trim() }).then((r) => r && onChanged())}>
            {t('access.approve')}
          </Button>
        </div>
      </form>
    );
  }
  const wait = REPORTING_CHANGE_KINDS.includes(change.kind) ? tr(change.needs_personal_data_approver ? 'approval.waitPersonalData' : 'approval.waitSecond') : t('access.waitSecond');
  return (
    <div className="grid gap-4">
      <Callout tone="info">
        {wait}{' '}
        <Link to="/admin/approvals" className="font-medium text-primary underline-offset-4 hover:underline">
          {t('access.openApprovals')}
        </Link>
      </Callout>
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}

/**
 * Proposal form: kind-specific fields + reason, then the next step on the same dialog. `proposed`
 * replaces the "recorded" sentence; `after` adds what comes next (e.g. a wizard's next step).
 */
export function ProposeForm({
  close,
  changed,
  base,
  fields,
  ready = true,
  submit,
  proposed,
  after,
}: DialogBodyProps & { base: Omit<AccessChangeProposal, 'reason'>; fields?: ReactNode; ready?: boolean; submit?: string; proposed?: string; after?: (change: AccessChange) => ReactNode }) {
  const { t } = useTranslation('operator');
  const [reason, setReason] = useState('');
  const w = useWrite((b: AccessChangeProposal, opts) => api.proposeAccessChange(b, opts));
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Callout tone="ok">{proposed ?? t('access.proposed')}</Callout>
        <NextStep change={w.state.result} onChanged={changed} close={close} />
        {after?.(w.state.result)}
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ ...base, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {fields}
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!ready || !reason.trim()} submit={submit ?? t('access.submitProposal')} onClose={close} />
    </form>
  );
}

function useRoleOptions() {
  const roles = useApi((signal) => api.listRoles({ signal }), []);
  return roles.status === 'success' ? roles.data.items : [];
}

function NewStaffFields({ close, changed }: DialogBodyProps) {
  const { t } = useTranslation('operator');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const options = useRoleOptions().filter((r) => r.code !== 'customer');
  return (
    <ProposeForm
      close={close}
      changed={changed}
      base={{ kind: 'CREATE_STAFF', email: email.trim(), display_name: name.trim(), roles }}
      ready={Boolean(email.trim() && name.trim())}
      fields={
        <>
          <Field label={t('access.email')}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" required />
          </Field>
          <Field label={t('access.displayName')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
          </Field>
          <Field label={t('access.roles')} hint={t('access.rolesHint')}>
            <div className="grid gap-1 sm:grid-cols-2">
              {options.map((r) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
                <label key={r.code} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={roles.includes(r.code)} onChange={(e) => setRoles(e.target.checked ? [...roles, r.code] : roles.filter((x) => x !== r.code))} />
                  {r.description}
                </label>
              ))}
            </div>
          </Field>
        </>
      }
    />
  );
}

/** "Add a staff member" (staff list). */
export function NewStaffButton({ onChanged }: { onChanged?: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('access.newStaff')} icon={UserPlus} title={t('access.newStaff')} description={t('access.newStaffDescription')} variant="default" wide onChanged={onChanged}>
      {(p) => <NewStaffFields {...p} />}
    </ActionDialog>
  );
}

/** The customer picker, reporting only the picked account's id. */
function AccountPicker({ onPicked }: { onPicked: (id: string | null) => void }) {
  const [customer, setCustomer] = useState<CustomerSummary | null>(null);
  return (
    <CustomerPicker
      value={customer}
      onChange={(c) => {
        setCustomer(c);
        onPicked(c?.id ?? null);
      }}
    />
  );
}

function RoleFields({ member, revoke, close, changed }: DialogBodyProps & { member: StaffMember; revoke: boolean }) {
  const { t } = useTranslation('operator');
  const options = useRoleOptions();
  const [grantKey, setGrantKey] = useState('');
  const [role, setRole] = useState('');
  const [scope, setScope] = useState<RoleScopeType>('GLOBAL');
  const [scopeId, setScopeId] = useState<string | null>(null);
  if (revoke) {
    const held = member.roles.find((g) => `${g.role_code}/${g.scope_type}/${g.scope_id ?? ''}` === grantKey);
    return (
      <ProposeForm
        close={close}
        changed={changed}
        base={{ kind: 'REVOKE_ROLE', user_id: member.id, role_code: held?.role_code, scope_type: held?.scope_type, scope_id: held?.scope_id ?? null }}
        ready={Boolean(held)}
        fields={
          <Field label={t('access.role')}>
            <select className={selectClass} value={grantKey} onChange={(e) => setGrantKey(e.target.value)} required>
              <option value="">{t('common.choose')}</option>
              {member.roles.map((g) => (
                <option key={`${g.role_code}/${g.scope_type}/${g.scope_id ?? ''}`} value={`${g.role_code}/${g.scope_type}/${g.scope_id ?? ''}`}>
                  {`${g.role_description}${g.scope_type === 'GLOBAL' ? '' : ` — ${g.scope_name ?? g.scope_type}`}`}
                </option>
              ))}
            </select>
          </Field>
        }
      />
    );
  }
  return (
    <ProposeForm
      close={close}
      changed={changed}
      base={{ kind: 'GRANT_ROLE', user_id: member.id, role_code: role, scope_type: scope, scope_id: scope === 'GLOBAL' ? null : scopeId }}
      ready={Boolean(role && (scope === 'GLOBAL' || scopeId))}
      fields={
        <>
          <Field label={t('access.role')}>
            <select className={selectClass} value={role} onChange={(e) => setRole(e.target.value)} required>
              <option value="">{t('common.choose')}</option>
              {options.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.description}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('access.scope')}>
            <select
              className={selectClass}
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as RoleScopeType);
                setScopeId(null);
              }}
            >
              <option value="GLOBAL">{t('access.scopeGlobal')}</option>
              <option value="CUSTOMER">{t('access.scopeCustomer')}</option>
            </select>
          </Field>
          {scope !== 'GLOBAL' ? (
            <Field label={t('access.scopeAccount')} group>
              <AccountPicker onPicked={setScopeId} />
            </Field>
          ) : null}
        </>
      }
    />
  );
}

type MemberAction = Extract<AccessChangeKind, 'DISABLE_STAFF' | 'ENABLE_STAFF' | 'RESET_MFA' | 'UNLOCK_STAFF' | 'GRANT_ROLE' | 'REVOKE_ROLE'>;

const ACTION_HINT: Partial<Record<MemberAction, 'access.disableHint' | 'access.resetMfaHint' | 'access.unlockHint'>> = {
  DISABLE_STAFF: 'access.disableHint',
  RESET_MFA: 'access.resetMfaHint',
  UNLOCK_STAFF: 'access.unlockHint',
};

/** "Change access" menu of a staff member's page. */
export function StaffAccessActions({ member, onChanged }: { member: StaffMember; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  const [open, setOpen] = useState<MemberAction | null>(null);
  const [dirty, setDirty] = useState(false);
  const close = () => {
    setOpen(null);
    if (dirty) {
      setDirty(false);
      onChanged();
    }
  };
  const items: { id: MemberAction; enabled: boolean }[] = [
    { id: 'GRANT_ROLE', enabled: true },
    { id: 'REVOKE_ROLE', enabled: member.roles.length > 0 },
    { id: 'UNLOCK_STAFF', enabled: member.locked },
    { id: 'RESET_MFA', enabled: true },
  ];
  const toggle: MemberAction = member.status === 'DISABLED' ? 'ENABLE_STAFF' : 'DISABLE_STAFF';
  const body = (a: MemberAction, p: DialogBodyProps) => {
    if (a === 'GRANT_ROLE' || a === 'REVOKE_ROLE') return <RoleFields {...p} member={member} revoke={a === 'REVOKE_ROLE'} />;
    const hint = ACTION_HINT[a];
    return <ProposeForm {...p} base={{ kind: a, user_id: member.id }} fields={hint ? <Callout tone={a === 'DISABLE_STAFF' ? 'warn' : 'info'}>{t(hint)}</Callout> : undefined} />;
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="sm">
            <ShieldCheck className="size-4" aria-hidden="true" /> {t('access.actions')} <ChevronDown className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {items.map((i) => (
            <DropdownMenuItem key={i.id} disabled={!i.enabled} onSelect={() => setOpen(i.id)}>
              {t(`access.kind.${i.id}`)}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem variant={toggle === 'DISABLE_STAFF' ? 'destructive' : 'default'} onSelect={() => setOpen(toggle)}>
            {t(`access.kind.${toggle}`)}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={open !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{open ? `${t(`access.kind.${open}`)} · ${member.display_name}` : ''}</DialogTitle>
            <DialogDescription>{t('access.pendingHint')}</DialogDescription>
          </DialogHeader>
          {open ? body(open, { close, changed: () => setDirty(true) }) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ChangeRow({ c, onChanged }: { c: AccessChange; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  const { t: tr } = useTranslation('reporting');
  const actionable = c.can_apply || c.can_decide || c.can_self_approve;
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 border-t px-5 py-3 first:border-t-0">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{t(`access.kind.${c.kind}`)}</span>
          <Badge tone={STATUS_TONE[c.status]}>{t(`access.status.${c.status}`)}</Badge>
        </div>
        <p className="text-sm">
          <TargetText c={c} />
        </p>
        <p className="text-xs text-muted-foreground">
          <span title={formatDateTime(c.requested_at)}>
            {t('access.requestedBy', { name: c.requested_by_name ?? '—' })} · {formatRelative(c.requested_at)}
          </span>
          {c.decided_by_name ? ` · ${t('access.decidedBy', { name: c.decided_by_name })}` : ''}
        </p>
        <p className="text-sm text-muted-foreground">{c.reason}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {actionable ? (
          <ActionDialog
            label={c.can_apply ? (takesKey(c.kind) ? tr('secret.takeKey') : t('access.apply')) : c.can_self_approve ? t('access.approveApply') : t('access.approve')}
            title={t(`access.kind.${c.kind}`)}
            description={c.reason}
            variant="default"
            onChanged={onChanged}
          >
            {(p) => <NextStep change={c} onChanged={p.changed} close={p.close} />}
          </ActionDialog>
        ) : null}
        {c.can_cancel ? (
          <ReasonAction
            label={t('access.withdraw')}
            title={`${t('access.withdraw')} · ${t(`access.kind.${c.kind}`)}`}
            run={(reason, opts) => api.cancelAccessChange(c.id, { reason }, opts)}
            done={() => t('access.status.CANCELLED')}
            onChanged={onChanged}
          />
        ) : null}
      </div>
    </li>
  );
}

/** Access changes still open (optionally about one staff member or only API keys). */
export function AccessChangesPanel({ userId, area, reloadKey, onChanged, hint }: { userId?: string; area?: 'STAFF' | 'API_CREDENTIAL' | 'REPORTING'; reloadKey?: unknown; onChanged?: () => void; hint?: string }) {
  const { t } = useTranslation('operator');
  const list = useApi((signal) => api.listAccessChanges({ state: 'OPEN', user_id: userId, area }, { signal }), [userId, area, reloadKey]);
  const changed = () => {
    list.reload();
    onChanged?.();
  };
  if (list.status === 'loading') return <Loading rows={1} />;
  // Nothing open: the panel stays out of the way (the approvals queue keeps the history).
  if (list.status === 'error' || list.data.items.length === 0) return null;
  return (
    <Section flush title={t('access.pendingTitle')} description={hint ?? t('access.pendingHint')}>
      <ul className="pb-2">
        {list.data.items.map((c) => (
          <ChangeRow key={c.id} c={c} onChanged={changed} />
        ))}
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// API keys

function IssueKeyFields({ close, changed }: DialogBodyProps) {
  const { t } = useTranslation('operator');
  const [owner, setOwner] = useState<string | null>(null);
  const [expires, setExpires] = useState('');
  return (
    <ProposeForm
      close={close}
      changed={changed}
      base={{ kind: 'ISSUE_API_CREDENTIAL', owner_type: 'CUSTOMER', owner_id: owner ?? undefined, expires_at: expires ? new Date(expires).toISOString() : null }}
      ready={Boolean(owner)}
      fields={
        <>
          <Field label={t('keys.owner')} group>
            <AccountPicker onPicked={setOwner} />
          </Field>
          <Field label={`${t('keys.expires')} (${t('common.optional')})`} hint={t('keys.expiresHint')}>
            <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="w-48" />
          </Field>
        </>
      }
    />
  );
}

/** "Issue an API key" (API credentials page). */
export function IssueApiKeyButton({ onChanged }: { onChanged?: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <ActionDialog label={t('keys.issue')} icon={KeyRound} title={t('keys.issue')} description={t('keys.issueDescription')} variant="default" wide onChanged={onChanged}>
      {(p) => <IssueKeyFields {...p} />}
    </ActionDialog>
  );
}

/** "Revoke" on one API key: takes effect at once. */
export function RevokeKeyButton({ credential, onChanged }: { credential: ApiCredential; onChanged: () => void }) {
  const { t } = useTranslation('operator');
  return (
    <ReasonAction
      label={t('keys.revoke')}
      title={`${t('keys.revokeTitle')} · ${credential.key_prefix}…`}
      warning={t('keys.revokeDescription')}
      danger
      disabled={credential.status !== 'ACTIVE'}
      run={(reason, opts) => api.revokeApiCredential(credential.id, { reason }, opts)}
      done={() => t('keys.revoked')}
      onChanged={onChanged}
    />
  );
}
